// src/pages/ApiKeys.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { Delete as DeleteIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { apiKeyService } from '../../api/apiKeyService';

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState(365);
  const [newKey, setNewKey] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const data = await apiKeyService.getAllKeys();
      if (data.isError) {
        setError(data.message || 'Failed to fetch API keys');
        setKeys([]);
      } else {
        setKeys(data);
        setError('');
      }
    } catch (err) {
      // This catch block might be redundant if handleApiError in service returns {isError: true}
      setError('Failed to fetch API keys due to a network or unexpected error.');
      console.error(err);
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the key');
      return;
    }
    setError(''); // Clear previous errors

    try {
      const data = await apiKeyService.createKey(
        newKeyName, 
        parseInt(newKeyExpiry, 10) || 365, // Ensure fallback if parsing fails or value is 0/empty
        []
      );
      if (data.isError) {
        setError(data.message || 'Failed to create new API key');
        setNewKey(null);
      } else {
        setNewKey(data); // data should be APIKeyCreatedResponse
        await fetchKeys(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to create new API key due to a network or unexpected error.');
      console.error(err);
      setNewKey(null);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    setError(''); // Clear previous errors

    try {
      const result = await apiKeyService.revokeKey(keyId);
      if (result.isError) {
        setError(result.message || 'Failed to revoke API key');
      } else {
        await fetchKeys(); // Refresh list
        setSnackbar({ open: true, message: 'API key revoked successfully' });
      }
    } catch (err) {
      setError('Failed to revoke API key due to a network or unexpected error.');
      console.error(err);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) {
        setError('Cannot copy an empty key.');
        setSnackbar({ open: true, message: 'Error: Key value is not available to copy.' });
        return;
    }
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbar({ open: true, message: 'API key copied to clipboard' });
      })
      .catch(() => {
        setError('Failed to copy API key to clipboard');
        setSnackbar({ open: true, message: 'Error: Could not copy key.' });
      });
  };

  const closeNewKeyDialog = () => {
    setOpenDialog(false);
    setNewKey(null);
    setNewKeyName('');
    setNewKeyExpiry(365);
    setError(''); // Clear error when closing dialog
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          API Key Management
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => { setOpenDialog(true); setError(''); /* Clear errors when opening */ }}
        >
          Create New Key
        </Button>
      </Box>
      
      {/* Moved general page error display here if needed, or keep dialog-specific error */}
      {/* {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>} */}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key Preview</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Loading...</TableCell>
              </TableRow>
            ) : keys.length === 0 && !error ? ( // Only show "No API keys" if no error fetching
              <TableRow>
                <TableCell colSpan={7} align="center">No API keys found</TableCell>
              </TableRow>
            ) : error && keys.length === 0 ? ( // Show error if keys are empty due to error
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{color: 'error.main'}}>{error}</TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {key.key_preview}
                      {/* Copying key_preview might not be intended, usually full key is copied from dialog */}
                      {/* If you want to copy preview, that's fine, but it's not the secure key */}
                      {/* <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(key.key_preview)}
                        sx={{ ml: 1 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton> */}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {new Date(key.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {key.expires_at 
                      ? new Date(key.expires_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {key.last_used_at 
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : 'Never used'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={key.is_active ? 'Active' : 'Revoked'} 
                      color={key.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {key.is_active && (
                      <IconButton 
                        color="error"
                        onClick={() => handleRevokeKey(key.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Create New Key Dialog */}
      <Dialog open={openDialog} onClose={closeNewKeyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {newKey ? 'New API Key Created' : 'Create New API Key'}
        </DialogTitle>
        <DialogContent>
          {/* Dialog-specific error display */}
          {error && !newKey && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {newKey ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This key will only be shown once. Please copy it now and store it securely.
              </Alert>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1 
              }}>
                <Typography 
                  variant="body2" 
                  fontFamily="monospace" 
                  sx={{ mr: 1, flexGrow: 1, wordBreak: 'break-all' }}
                >
                  {newKey.key_value} {/* CORRECTED */}
                </Typography>
                <IconButton onClick={() => copyToClipboard(newKey.key_value)}> {/* CORRECTED */}
                  <CopyIcon />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                autoFocus
                label="Key Name"
                fullWidth
                margin="normal"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production API Key"
                required
              />
              <TextField
                label="Expires In (Days)"
                type="number"
                fullWidth
                margin="normal"
                value={newKeyExpiry}
                onChange={(e) => setNewKeyExpiry(e.target.value)}
                helperText="Set to 0 or leave empty for non-expiring. Max 365 days."
                inputProps={{ min: 0, max: 365 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {newKey ? (
            <Button onClick={closeNewKeyDialog}>Close</Button>
          ) : (
            <>
              <Button onClick={closeNewKeyDialog}>Cancel</Button>
              <Button variant="contained" onClick={handleCreateKey} disabled={!newKeyName.trim()}>Create</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default ApiKeys;